import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { clearUser } from '../store/slices/authslice';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import AuthDropdown from './AuthDropdown';
import { Helmet } from 'react-helmet';

const Navbar = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const cartItems = useSelector((state) => state.cart.items);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthDropdownOpen, setIsAuthDropdownOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [neonState, setNeonState] = useState(true);
  
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const cartItemCount = cartItems.reduce((total, item) => total + item.quantity, 0);

  // Neon flickering effect
  useEffect(() => {
    const flickerInterval = setInterval(() => {
      setNeonState(prev => !prev);
    }, 500);
    
    // Random flicker effect
    const randomFlicker = () => {
      const randomDelay = Math.floor(Math.random() * 2000) + 100;
      setTimeout(() => {
        setNeonState(prev => !prev);
        setTimeout(() => {
          setNeonState(prev => !prev);
          randomFlicker();
        }, 100);
      }, randomDelay);
    };
    
    randomFlicker();
    
    return () => {
      clearInterval(flickerInterval);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      dispatch(clearUser());
      setIsDropdownOpen(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
    setIsAuthDropdownOpen(false);
  };

  // Neon styles
  const neonStyle = {
    textShadow: neonState 
      ? '0 0 5px #ff0000, 0 0 10px #ff0000, 0 0 15px #ff0000, 0 0 20px #ff0000'
      : 'none',
    transition: 'text-shadow 0.1s ease',
    fontWeight: 'bold',
    color: neonState ? '#ff6b6b' : '#ff0000',
  };

  return (
    <>
      <Helmet>
        <title>The Grill Shack - Premium Grilled Delicacies</title>
        <meta name="description" content="Experience the finest grilled dishes at The Grill Shack. Order online for pickup or delivery." />
        <meta name="keywords" content="grill, restaurant, food delivery, grilled food, Nigerian cuisine" />
        <meta property="og:title" content="The Grill Shack - Premium Grilled Delicacies" />
        <meta property="og:description" content="Experience the finest grilled dishes at The Grill Shack. Order online for pickup or delivery." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://thegrillshackng.com" />
      </Helmet>

      <nav className="bg-black sticky top-0 z-50 border-b-2 border-red-500" role="navigation" aria-label="Main navigation">
        <div className="max-w-screen-xl px-4 mx-auto 2xl:px-0 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/" className="shrink-0" aria-label="Home">
                <img 
                  className="w-auto h-10"
                  src="https://res.cloudinary.com/dgqu4ohbw/image/upload/v1740939535/grillsd-1_ltzsfb.png" 
                  alt="The Grill Shack Logo"
                  width="40"
                  height="40"
                />
              </Link>

              <ul className="hidden lg:flex items-center justify-start gap-4 md:gap-6 py-2" role="menubar">
                <li role="none">
                  <Link to="/" className="text-sm font-medium text-red-500 hover:text-red-700" role="menuitem">
                    Home
                  </Link>
                </li>
                <li role="none">
                  <Link to="/menu/breakfast" className="text-sm font-medium text-red-500 hover:text-red-700" role="menuitem">
                    Menu
                  </Link>
                </li>
                <li role="none">
                  <Link 
                    to="/farm" 
                    className="text-sm font-medium" 
                    style={neonStyle} 
                    role="menuitem"
                  >
                    Ram Farm
                  </Link>
                </li>
              </ul>
            </div>

            <div className="flex items-center space-x-2">
              {/* Always visible Menu link for mobile */}
              <Link 
                to="/menu/grills" 
                className="lg:hidden text-sm font-medium text-red-500 hover:text-red-700 px-2 py-1"
                aria-label="View Menu"
              >
                Menu
              </Link>

              {/* Farm link for mobile */}
              <Link 
                to="/farm" 
                className="lg:hidden text-sm font-medium px-2 py-1"
                style={neonStyle}
                aria-label="View Ram Farm"
              >
                Ram Farm
              </Link>

              <Link 
                to="/cart" 
                className="relative inline-flex items-center rounded-lg justify-center p-1 hover:bg-gray-800 text-sm font-medium text-red-500"
                aria-label={`Shopping Cart with ${cartItemCount} items`}
              >
                <svg className="w-5 h-5 lg:me-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 4h1.5L9 16m0 0h8m-8 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm8 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm-8.5-3h9.25L19 7H7.312"/>
                </svg>
                {cartItemCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {cartItemCount}
                  </span>
                )}
                <span className="hidden sm:flex ml-1">Cart</span>
              </Link>

              {!isAuthenticated ? (
                <div className="relative" ref={dropdownRef}>
                  <button 
                    onClick={() => {
                      setIsAuthDropdownOpen(!isAuthDropdownOpen);
                      setIsDropdownOpen(false);
                    }}
                    className="inline-flex items-center rounded-lg justify-center p-2 hover:bg-gray-800 text-sm font-medium text-red-500"
                    aria-expanded={isAuthDropdownOpen}
                    aria-controls="auth-dropdown"
                  >
                    <svg className="w-5 h-5 sm:me-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeWidth="2" d="M7 17v1a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1a3 3 0 0 0-3-3h-4a3 3 0 0 0-3 3Zm8-9a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/>
                    </svg>
                    <span className="flex">Login / Sign Up</span>
                  </button>

                  <AuthDropdown 
                    isOpen={isAuthDropdownOpen}
                    onClose={() => setIsAuthDropdownOpen(false)}
                    initialMode={authMode}
                  />
                </div>
              ) : (
                <div className="relative" ref={dropdownRef}>
                  <button 
                    onClick={toggleDropdown}
                    className="inline-flex items-center rounded-lg justify-center p-1 hover:bg-gray-800 text-sm font-medium text-red-500"
                    aria-expanded={isDropdownOpen}
                    aria-controls="user-dropdown"
                  >
                    <svg className="w-5 h-5 me-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeWidth="2" d="M7 17v1a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1a3 3 0 0 0-3-3h-4a3 3 0 0 0-3 3Zm8-9a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/>
                    </svg>
                    <span className="hidden sm:flex">{user?.username}</span>
                    <svg className="hidden sm:flex w-3 h-3 ms-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 9-7 7-7-7"/>
                    </svg>
                  </button>

                  {isDropdownOpen && (
                    <div 
                      id="user-dropdown"
                      className="absolute right-0 mt-2 w-56 rounded-lg bg-black text-white shadow-lg"
                      role="menu"
                    >
                      <div className="p-2 space-y-1">
                        <Link to="/account" className="block px-4 py-2 text-sm hover:bg-gray-700 rounded-md" role="menuitem">My Account</Link>
                        <Link to="/orders" className="block px-4 py-2 text-sm hover:bg-gray-700 rounded-md" role="menuitem">My Orders</Link>
                        <button
                          onClick={handleSignOut}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-gray-700 rounded-md"
                          role="menuitem"
                        >
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div ref={mobileMenuRef}>
                <button 
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="lg:hidden inline-flex items-center justify-center p-2 hover:bg-gray-800 rounded-md text-red-500"
                  aria-expanded={isMobileMenuOpen}
                  aria-label="Toggle mobile menu"
                >
                  <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeWidth="2" d="M5 7h14M5 12h14M5 17h14"/>
                  </svg>
                </button>

                {isMobileMenuOpen && (
                  <div 
                    className="absolute right-0 mt-2 w-48 rounded-lg bg-black text-white shadow-lg lg:hidden"
                    role="menu"
                  >
                    <div className="py-2">
                      <Link to="/" className="block px-4 py-2 text-sm text-red-500 hover:bg-gray-700" role="menuitem">Home</Link>
                      <Link to="/menu/breakfast" className="block px-4 py-2 text-sm text-red-500 hover:bg-gray-700" role="menuitem">Menu</Link>
                      <Link to="/farm" className="block px-4 py-2 text-sm hover:bg-gray-700" role="menuitem" style={neonStyle}>Ram Farm</Link>
                      <Link to="/cart" className="block px-4 py-2 text-sm text-red-500 hover:bg-gray-700" role="menuitem">
                        Cart {cartItemCount > 0 && `(${cartItemCount})`}
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;